/**
 * Lab Result Detail Page - Split View Layout
 *
 * Side-by-side view with document preview on left and biomarker form on right.
 * Supports nurse verification workflow with action choice (assign doctor / skip review).
 */
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Show } from '@refinedev/antd'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Modal,
  Spin,
  Result,
  Alert,
  Row,
  Col,
  Timeline,
  message,
} from 'antd'
import {
  SendOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import { DocumentPreview } from '../../components/DocumentPreview'
import { BiomarkerEntryForm } from '../../components/BiomarkerEntryForm'
import { VerificationActions } from '../../components/VerificationActions'
import { DoctorReviewActions } from '../../components/DoctorReviewActions'
import {
  BiomarkerValue,
  initializeBiomarkerValues,
} from '../../lib/biomarker-config'

const { Text } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

type LabResultStatus =
  | 'PENDING'
  | 'RECEIVED'
  | 'VERIFIED'
  | 'AWAITING_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'SHARED_WITH_PATIENT'

type LabResultSource = 'AQUALAB_EMAIL' | 'PATIENT_UPLOAD' | 'NURSE_MANUAL'

interface ExistingBiomarkerResult {
  code: string
  name: string
  value: number
  unit: string
  referenceMin?: number
  referenceMax?: number
}

interface LabResultDetail {
  id: string
  labName: string
  labReference: string | null
  sampleDate: string
  reportDate: string | null
  source: LabResultSource
  status: LabResultStatus
  results: ExistingBiomarkerResult[]
  verificationNotes: string | null
  verifiedBy: string | null
  verifiedAt: string | null
  reviewNotes: string | null
  reviewDecision: string | null
  reviewedAt: string | null
  sharedAt: string | null
  createdAt: string
  user: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    phone: string | null
    image: string | null
    dateOfBirth: string | null
    gender: string | null
    profile: {
      address: string | null
      city: string | null
    } | null
  }
  assignedDoctor: {
    id: string
    user: {
      firstName: string
      lastName: string
      image: string | null
    }
  } | null
  documents: {
    id: string
    fileName: string
    fileUrl: string
    fileType: string
  }[]
}

const STATUS_CONFIG: Record<LabResultStatus, { color: string; text: string }> = {
  PENDING: { color: 'default', text: 'Na čekanju' },
  RECEIVED: { color: 'processing', text: 'Primljeno' },
  VERIFIED: { color: 'purple', text: 'Verifikovano' },
  AWAITING_REVIEW: { color: 'warning', text: 'Čeka pregled lekara' },
  APPROVED: { color: 'success', text: 'Odobreno' },
  REJECTED: { color: 'error', text: 'Odbijeno' },
  SHARED_WITH_PATIENT: { color: 'success', text: 'Deljeno sa pacijentom' },
}

const SOURCE_LABELS: Record<LabResultSource, string> = {
  AQUALAB_EMAIL: 'AquaLab Email',
  PATIENT_UPLOAD: 'Upload pacijenta',
  NURSE_MANUAL: 'Ručni unos',
}

export default function LabResultShowPage() {
  const { id } = useParams<{ id: string }>()
  const [messageApi, contextHolder] = message.useMessage()

  const [data, setData] = useState<LabResultDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Biomarker state (using new config)
  const [biomarkers, setBiomarkers] = useState<BiomarkerValue[]>([])

  // Dialogs
  const [showShareDialog, setShowShareDialog] = useState(false)

  // Fetch data
  const fetchData = async () => {
    setIsLoading(true)
    setIsError(false)
    try {
      const resultRes = await fetch(`${API_URL}/admin/lab-results/${id}`, {
        headers: getAuthHeaders(),
      })

      if (!resultRes.ok) throw new Error('Failed to fetch lab result')

      const resultJson: LabResultDetail = await resultRes.json()
      setData(resultJson)

      // Initialize biomarkers with TRT panel + existing values
      const initialBiomarkers = initializeBiomarkerValues(resultJson.results)
      setBiomarkers(initialBiomarkers)
    } catch (error) {
      console.error('Error fetching lab result:', error)
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getPatientName = () => {
    if (!data) return ''
    if (data.user.firstName || data.user.lastName) {
      return `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim()
    }
    return data.user.email
  }

  const handleShare = async () => {
    setIsSaving(true)
    try {
      const res = await fetch(`${API_URL}/admin/lab-results/${id}/share`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sendNotification: true }),
      })
      if (!res.ok) throw new Error('Failed to share')
      setShowShareDialog(false)
      messageApi.success('Rezultati deljeni sa pacijentom')
      await fetchData()
    } catch (error) {
      console.error('Error sharing:', error)
      messageApi.error('Greška pri deljenju')
    } finally {
      setIsSaving(false)
    }
  }

  const handleVerificationSuccess = () => {
    fetchData()
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 400,
        }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Result
        status="error"
        title="Greška"
        subTitle="Greška pri učitavanju lab rezultata"
        extra={
          <Link to="/lab-results">
            <Button type="primary">Nazad na listu</Button>
          </Link>
        }
      />
    )
  }

  // Determine what mode we're in
  const isVerificationMode = data.status === 'PENDING' || data.status === 'RECEIVED'
  const isAwaitingDoctorReview = data.status === 'AWAITING_REVIEW'
  const canShare = data.status === 'APPROVED'
  const isComplete = data.status === 'SHARED_WITH_PATIENT'
  const isEditable = isVerificationMode || data.status === 'VERIFIED'

  // Build timeline items
  const timelineItems = [
    {
      color: 'blue' as const,
      children: (
        <div>
          <Text strong>Kreirano</Text>
          <br />
          <Text type="secondary">{formatDateTime(data.createdAt)}</Text>
        </div>
      ),
    },
    ...(data.verifiedAt
      ? [
          {
            color: 'purple' as const,
            children: (
              <div>
                <Text strong>Verifikovano</Text>
                <br />
                <Text type="secondary">{formatDateTime(data.verifiedAt)}</Text>
              </div>
            ),
          },
        ]
      : []),
    ...(data.assignedDoctor && data.status !== 'VERIFIED'
      ? [
          {
            color: 'orange' as const,
            children: (
              <div>
                <Text strong>Dodeljeno lekaru</Text>
                <br />
                <Text type="secondary">
                  Dr. {data.assignedDoctor.user.firstName}{' '}
                  {data.assignedDoctor.user.lastName}
                </Text>
              </div>
            ),
          },
        ]
      : []),
    ...(data.reviewedAt
      ? [
          {
            color: 'cyan' as const,
            children: (
              <div>
                <Text strong>Pregledano</Text>
                <br />
                <Text type="secondary">{formatDateTime(data.reviewedAt)}</Text>
                {data.reviewDecision && (
                  <Tag
                    color={data.reviewDecision === 'APPROVED' ? 'success' : 'error'}
                    style={{ marginLeft: 8 }}
                  >
                    {data.reviewDecision === 'APPROVED' ? 'Odobreno' : 'Odbijeno'}
                  </Tag>
                )}
              </div>
            ),
          },
        ]
      : []),
    ...(data.sharedAt
      ? [
          {
            color: 'green' as const,
            children: (
              <div>
                <Text strong>Deljeno sa pacijentom</Text>
                <br />
                <Text type="secondary">{formatDateTime(data.sharedAt)}</Text>
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      {contextHolder}
      <Show
        title={
          <Space>
            <Link to="/lab-results">
              <Button type="text" icon={<ArrowLeftOutlined />} size="small" />
            </Link>
            <span>{getPatientName()}</span>
            <Text type="secondary">- {data.labName}</Text>
          </Space>
        }
        headerButtons={
          <Space>
            <Tag color={STATUS_CONFIG[data.status].color} style={{ fontSize: 14 }}>
              {STATUS_CONFIG[data.status].text}
            </Tag>
            <Tag>{SOURCE_LABELS[data.source]}</Tag>
          </Space>
        }
        contentProps={{ style: { padding: 0 } }}
      >
        {/* Header Info Bar */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col span={12}>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Pacijent">
                  <Link to={`/patients/${data.user.id}`}>{getPatientName()}</Link>
                </Descriptions.Item>
                <Descriptions.Item label="Email">{data.user.email}</Descriptions.Item>
                <Descriptions.Item label="Laboratorija">{data.labName}</Descriptions.Item>
                <Descriptions.Item label="Datum uzorka">
                  {formatDate(data.sampleDate)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              {isAwaitingDoctorReview && data.assignedDoctor && (
                <Alert
                  message={`Čeka pregled: Dr. ${data.assignedDoctor.user.firstName} ${data.assignedDoctor.user.lastName}`}
                  type="warning"
                  showIcon
                  icon={<ClockCircleOutlined />}
                />
              )}
              {canShare && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => setShowShareDialog(true)}
                  style={{ backgroundColor: '#52c41a' }}
                >
                  Podeli sa pacijentom
                </Button>
              )}
              {isComplete && (
                <Alert
                  message="Rezultati su deljeni sa pacijentom"
                  type="success"
                  showIcon
                />
              )}
            </Col>
          </Row>
        </Card>

        {/* Main Split View */}
        <Row gutter={16}>
          {/* Left Column - Document Preview */}
          <Col xs={24} lg={12}>
            <DocumentPreview documents={data.documents} height="calc(100vh - 250px)" />
          </Col>

          {/* Right Column - Biomarkers + Actions */}
          <Col xs={24} lg={12}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Biomarker Entry Form */}
              <BiomarkerEntryForm
                values={biomarkers}
                onValuesChange={setBiomarkers}
                disabled={!isEditable}
              />

              {/* Verification Actions (only in verification mode) */}
              {isVerificationMode && (
                <VerificationActions
                  labResultId={data.id}
                  biomarkers={biomarkers}
                  onSuccess={handleVerificationSuccess}
                  currentStatus={data.status}
                />
              )}

              {/* Doctor Review Actions (only when awaiting doctor review) */}
              {isAwaitingDoctorReview && (
                <DoctorReviewActions
                  labResultId={data.id}
                  onSuccess={handleVerificationSuccess}
                  patientName={getPatientName()}
                />
              )}

              {/* Already verified info */}
              {data.status === 'VERIFIED' && !data.assignedDoctor && (
                <Card>
                  <Alert
                    message="Rezultat je verifikovan"
                    description="Čeka na dodeljivanje lekaru ili odobrenje."
                    type="info"
                    showIcon
                  />
                </Card>
              )}

              {/* Doctor review notes (if reviewed) */}
              {data.reviewNotes && (
                <Card
                  title="Beleške lekara"
                  size="small"
                >
                  <Text>{data.reviewNotes}</Text>
                </Card>
              )}

              {/* Verification notes (if any) */}
              {data.verificationNotes && (
                <Card title="Beleške verifikacije" size="small">
                  <Text>{data.verificationNotes}</Text>
                </Card>
              )}
            </div>
          </Col>
        </Row>

        {/* Timeline at bottom */}
        <Card title="Istorija" size="small" style={{ marginTop: 16 }}>
          <Timeline items={timelineItems} mode="left" />
        </Card>
      </Show>

      {/* Share Modal */}
      <Modal
        title="Podeli sa pacijentom"
        open={showShareDialog}
        onCancel={() => setShowShareDialog(false)}
        onOk={handleShare}
        okText="Podeli"
        cancelText="Odustani"
        confirmLoading={isSaving}
        okButtonProps={{ style: { backgroundColor: '#52c41a' } }}
      >
        <p>
          Pacijent će dobiti obaveštenje i moći će da vidi rezultate na svom portalu.
        </p>
      </Modal>
    </>
  )
}
